// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import type { ReactNode } from 'react';

export type RecipientInfoCardProps = Readonly<{
  /** Display name shown as the card heading. */
  title: string;
  /** Email address — rendered on the same line as {@code phone}. Either may be null. */
  email?: string | null;
  /** Optional phone number. */
  phone?: string | null;
  /** Optional extra content (agency code row, badges, etc.) below the primary line. */
  children?: ReactNode;
}>;

/**
 * Shared recipient header used at the top of every advisor compose
 * surface (LOA invitation wizard, Resend invitation, CoA wizard,
 * Nudge Provider page). Renders the recipient name + the
 * {@code phone | email} secondary line — the canonical layout the
 * three legacy pages were each duplicating inline.
 *
 * <p>Use {@link children} to extend with doc-type-specific metadata
 * (e.g. agency code row on the CoA nudge page).
 */
export function RecipientInfoCard({
  title,
  email,
  phone,
  children,
}: RecipientInfoCardProps) {
  const hasContactLine = phone || email;
  return (
    <div className="px-lg py-md flex flex-col gap-sm bg-white">
      <h2 className="text-black text-h5 font-medium font-sans !m-0">
        {title}
      </h2>
      {hasContactLine && (
        <div className="flex items-center gap-sm">
          {phone && (
            <p className="!mb-0 text-grey-04 text-base font-normal font-sans">
              {phone}
            </p>
          )}
          {phone && email && (
            <p className="!mb-0 text-grey-04 text-base font-normal font-sans">
              &nbsp;|&nbsp;
            </p>
          )}
          {email && (
            <p className="!mb-0 text-grey-04 text-base font-normal font-sans">
              {email}
            </p>
          )}
        </div>
      )}
      {children}
    </div>
  );
}

export default RecipientInfoCard;
